import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import '../../css/forms/ApplicationForms.css';

const API_URL = 'https://rm-backend-service-505761517154.us-central1.run.app/submit-ambassador-2026';

const CampusAmbassador2026 = () => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    university: '',
    graduation_year: '',
    college_address: '',
    work_status: '',
    past_experience: '',
    interest_reason: '',
    additional_notes: '',
    resume_url: '',
    instagram_url: '',
    tiktok_url: '',
    cover_letter_url: '',
    how_did_you_hear: '',
    referral_name: '',
    other_source: ''
  });

  const [formErrors, setFormErrors] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [showThankYouModal, setShowThankYouModal] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Clear conditional fields when how_did_you_hear changes
    if (name === 'how_did_you_hear') {
      setFormData(prev => ({ 
        ...prev, 
        [name]: value,
        referral_name: '',
        other_source: ''
      }));
      // Clear errors for conditional fields
      if (submitAttempted) {
        setFormErrors(prev => ({ 
          ...prev, 
          [name]: '',
          referral_name: '',
          other_source: ''
        }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
      if (submitAttempted && formErrors[name]) {
        setFormErrors(prev => ({ ...prev, [name]: '' }));
      }
    }
  };

  const validateForm = () => {
    const errors = {};
    const requiredFields = [
      'first_name', 'last_name', 'email', 'phone', 
      'university', 'graduation_year', 'college_address',
      'work_status', 'past_experience', 'interest_reason',
      'resume_url', 'instagram_url', 'tiktok_url', 'how_did_you_hear'
    ];

    requiredFields.forEach(field => {
      if (!formData[field] || (typeof formData[field] === 'string' && !formData[field].trim())) {
        errors[field] = 'This field is required';
      }
    });

    // Conditional validation for referral and other
    if (formData.how_did_you_hear === 'Referral' && (!formData.referral_name || !formData.referral_name.trim())) {
      errors.referral_name = 'Please provide the name of the person who referred you';
    }

    if (formData.how_did_you_hear === 'Other' && (!formData.other_source || !formData.other_source.trim())) {
      errors.other_source = 'Please specify how you heard about this role';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitAttempted(true);
    setIsSubmitting(true);

    if (validateForm()) {
      try {
        const response = await fetch(API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        });

        const contentType = response.headers.get("content-type");

        if (!response.ok) {
          if (contentType && contentType.includes("application/json")) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to submit application');
          } else {
            const errorText = await response.text();
            throw new Error(`Server error: ${errorText.slice(0, 100)}...`);
          }
        }

        const data = await response.json();
        console.log('Success:', data);
        setSubmitted(true);
        setShowThankYouModal(true);
        window.scrollTo(0, 0);

      } catch (error) {
        console.error('Error submitting form:', error.message);
        alert(error.message || 'An error occurred. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    } else {
      setIsSubmitting(false);
      // Only scroll to error if there are validation errors
      setTimeout(() => {
        const firstErrorField = document.querySelector('.error');
        if (firstErrorField) {
          firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  };

  const closeModal = () => {
    setShowThankYouModal(false);
    navigate('/roles');
    window.scrollTo(0, 0);
  };

  if (submitted) {
    return (
      <div className="aam-container thank-you-page">
        <Helmet>
          <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap" 
          rel="stylesheet" />
        </Helmet>
        
        {showThankYouModal && (
          <div className="thank-you-modal">
            <div className="thank-you-content">
              <h1>Thank You for Applying!</h1>
              <p>We've received your application for the Campus Ambassador Program.</p>
              <p>If we feel you're a good fit for the role, we'll email you with next steps soon.</p>
              <button onClick={closeModal}>Go to Roles</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="aam-container">
      <Helmet>
        <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap" 
        rel="stylesheet" />
      </Helmet>
      
      <div className="aam-header">
        <h1>Campus Ambassador</h1>
        <div className="aam-meta">
          <span className="meta-item">Location: Your Campus</span>
          <span className="meta-item">Job Type: Part-Time / Flexible</span>
        </div>
      </div>

      <div className="aam-content">
        <section className="aam-section aam-about">
          <h2>About the Role</h2>
          <p>Join one of the most exciting and rewarding ambassador roles designed to empower students to connect, create, and inspire! As a Campus Ambassador, you'll represent cool brands on your campus — helping spread awareness, host engaging events, and bring students together through impactful experiences.</p>
          <p>We're looking for energetic, outgoing, and motivated students who are passionate about community building, marketing, and making a difference.</p>
        </section>

        <section className="aam-section aam-description">
          <h2>Why Join?</h2>
          <ul className="aam-list">
            <li><strong>Competitive Pay & Bonuses:</strong> Earn a competitive stipend plus performance-based bonuses for successful events and activations.</li>
            <li><strong>Real-World Experience:</strong> Build marketing, event management, and communication skills that stand out on your resume.</li>
            <li><strong>Networking Opportunities:</strong> Connect with industry professionals, brand teams, and other ambassadors nationwide.</li>
          </ul>
        </section>

        <section className="aam-section aam-responsibilities">
          <h2>What You'll Do</h2>
          <ul className="aam-list">
            <li><strong>Promote & Educate:</strong> Spread awareness about the brand through on-campus engagement, digital campaigns, and word-of-mouth marketing.</li>
            <li><strong>Host Events:</strong> Plan and execute fun, interactive tabling events, giveaways, and pop-ups to drive visibility and participation.</li>
            <li><strong>Collaborate:</strong> Partner with student organizations and campus groups to integrate the brand into existing student life activities.</li>
            <li><strong>Share Online:</strong> Amplify your impact through social media by sharing updates, stories, and event highlights.</li>
          </ul>
        </section>

        <section className="aam-section aam-benefits">
          <h2>What We're Looking For</h2>
          <ul className="aam-list">
            <li>Currently enrolled college students with a strong presence on campus.</li>
            <li>Outgoing, reliable, and creative individuals who love connecting with others.</li>
            <li>Organized multitaskers who enjoy planning and promoting events.</li>
            <li>Team players with a passion for storytelling, marketing, and brand growth.</li>
          </ul>
        </section>

        <section className="aam-section aam-application">
          <h2>Program Details</h2>
          <ul className="aam-list">
            <li><strong>Time Commitment:</strong> Approx. 15–20 hours per month</li>
            <li><strong>Duration:</strong> Flexible semester or year-long opportunities</li>
            <li><strong>Compensation:</strong> Competitive hourly rate + bonuses for top performance</li>
          </ul>
        </section>

        <form onSubmit={handleSubmit} className="aam-form">
          <h2 className="form-title">Application Form</h2>
          
          {submitAttempted && Object.keys(formErrors).length > 0 && (
            <div className="form-error-message">
              Please correct the highlighted errors:
            </div>
          )}

          <div className="form-grid">
            <div className="form-group">
              <label className="question-text">First Name *</label>
              <input 
                type="text" 
                name="first_name" 
                value={formData.first_name} 
                onChange={handleChange} 
                className={formErrors.first_name ? 'error' : ''}
              />
              {formErrors.first_name && <span className="error-message">{formErrors.first_name}</span>}
            </div>

            <div className="form-group">
              <label className="question-text">Last Name *</label>
              <input 
                type="text" 
                name="last_name" 
                value={formData.last_name} 
                onChange={handleChange} 
                className={formErrors.last_name ? 'error' : ''}
              />
              {formErrors.last_name && <span className="error-message">{formErrors.last_name}</span>}
            </div>

            <div className="form-group">
              <label className="question-text">Student Email *</label>
              <input 
                type="email" 
                name="email" 
                value={formData.email} 
                onChange={handleChange} 
                className={formErrors.email ? 'error' : ''}
              />
              {formErrors.email && <span className="error-message">{formErrors.email}</span>}
            </div>

            <div className="form-group">
              <label className="question-text">Phone *</label>
              <input 
                type="tel" 
                name="phone" 
                value={formData.phone} 
                onChange={handleChange} 
                className={formErrors.phone ? 'error' : ''}
              />
              {formErrors.phone && <span className="error-message">{formErrors.phone}</span>}
            </div>

            <div className="form-group">
              <label className="question-text">University/College Name *</label>
              <input 
                type="text" 
                name="university" 
                value={formData.university} 
                onChange={handleChange} 
                className={formErrors.university ? 'error' : ''}
              />
              {formErrors.university && <span className="error-message">{formErrors.university}</span>}
            </div>

            <div className="form-group">
              <label className="question-text">Expected Year of Graduation *</label>
              <input 
                type="text" 
                name="graduation_year" 
                value={formData.graduation_year} 
                onChange={handleChange} 
                className={formErrors.graduation_year ? 'error' : ''}
              />
              {formErrors.graduation_year && <span className="error-message">{formErrors.graduation_year}</span>}
            </div>

            <div className="form-group full-width">
              <label className="question-text">What is your college address - dorm, apartment, housing, etc.? *</label>
              <input 
                type="text" 
                name="college_address" 
                value={formData.college_address} 
                onChange={handleChange} 
                className={formErrors.college_address ? 'error' : ''}
              />
              {formErrors.college_address && <span className="error-message">{formErrors.college_address}</span>}
            </div>

            <div className="form-group full-width">
              <label className="question-text">Are you a U.S. citizen or authorized to work in the United States? *</label>
              <div className="radio-group">
                <label className="label-select">
                  <input
                    type="radio"
                    name="work_status"
                    value="yes"
                    checked={formData.work_status === "yes"}
                    onChange={handleChange}
                  />
                  Yes
                </label>
                <label className="label-select">
                  <input
                    type="radio"
                    name="work_status"
                    value="no"
                    checked={formData.work_status === "no"}
                    onChange={handleChange}
                  />
                  No
                </label>
              </div>
              {formErrors.work_status && <span className="error-message">{formErrors.work_status}</span>}
            </div>

            <div className="form-group full-width">
              <label className="question-text">Have you previously served as a campus ambassador? 
                If yes, which company or organization was it for, and what were your main responsibilities? *</label>
              <textarea 
                name="past_experience" 
                value={formData.past_experience} 
                onChange={handleChange} 
                className={formErrors.past_experience ? 'error' : ''}
                rows="3"
              />
              {formErrors.past_experience && <span className="error-message">{formErrors.past_experience}</span>}
            </div>

            <div className="form-group full-width">
              <label className="question-text">What clubs, organizations, or communities on campus are you involved in, 
                and how would you leverage those for this role? Why are you interested in becoming a Campus Ambassador? *</label>
              <textarea 
                name="interest_reason" 
                value={formData.interest_reason} 
                onChange={handleChange} 
                className={formErrors.interest_reason ? 'error' : ''}
                rows="3"
              />
              {formErrors.interest_reason && <span className="error-message">{formErrors.interest_reason}</span>}
            </div>

            <div className="form-group full-width">
              <label className="question-text">Resume URL * (Google Drive, Dropbox, etc.)</label>
              <input 
                type="text" 
                name="resume_url" 
                value={formData.resume_url} 
                onChange={handleChange} 
                placeholder="drive.google.com/your-resume"
                className={formErrors.resume_url ? 'error' : ''}
              />
              {formErrors.resume_url && <span className="error-message">{formErrors.resume_url}</span>}
            </div>

            <div className="form-group full-width">
              <label className="question-text">Cover Letter URL (optional)</label>
              <input 
                type="text" 
                name="cover_letter_url" 
                value={formData.cover_letter_url} 
                onChange={handleChange} 
                placeholder="drive.google.com/your-cover-letter"
              />
            </div>

            <div className="form-group full-width">
              <label className="question-text">Instagram URL *</label>
              <input 
                type="text" 
                name="instagram_url" 
                value={formData.instagram_url} 
                onChange={handleChange} 
                placeholder="instagram.com/your-username"
                className={formErrors.instagram_url ? 'error' : ''}
              />
              {formErrors.instagram_url && <span className="error-message">{formErrors.instagram_url}</span>}
            </div>

            <div className="form-group full-width">
              <label className="question-text">TikTok URL *</label>
              <input 
                type="text" 
                name="tiktok_url" 
                value={formData.tiktok_url} 
                onChange={handleChange} 
                placeholder="tiktok.com/@your-username"
                className={formErrors.tiktok_url ? 'error' : ''}
              />
              {formErrors.tiktok_url && <span className="error-message">{formErrors.tiktok_url}</span>}
            </div>

            <div className="form-group full-width">
              <label className="question-text">How did you hear about this role? *</label>
              <div className="radio-group">
                <label className="label-select">
                  <input
                    type="radio"
                    name="how_did_you_hear"
                    value="LinkedIn"
                    checked={formData.how_did_you_hear === "LinkedIn"}
                    onChange={handleChange}
                  />
                  LinkedIn
                </label>
                <label className="label-select">
                  <input
                    type="radio"
                    name="how_did_you_hear"
                    value="Handshake"
                    checked={formData.how_did_you_hear === "Handshake"}
                    onChange={handleChange}
                  />
                  Handshake
                </label>
                <label className="label-select">
                  <input
                    type="radio"
                    name="how_did_you_hear"
                    value="SideShift"
                    checked={formData.how_did_you_hear === "SideShift"}
                    onChange={handleChange}
                  />
                  SideShift
                </label>
                <label className="label-select">
                  <input
                    type="radio"
                    name="how_did_you_hear"
                    value="Referral"
                    checked={formData.how_did_you_hear === "Referral"}
                    onChange={handleChange}
                  />
                  Referral
                </label>
                <label className="label-select">
                  <input
                    type="radio"
                    name="how_did_you_hear"
                    value="Other"
                    checked={formData.how_did_you_hear === "Other"}
                    onChange={handleChange}
                  />
                  Other
                </label>
              </div>
              {formErrors.how_did_you_hear && <span className="error-message">{formErrors.how_did_you_hear}</span>}
              
              {formData.how_did_you_hear === 'Referral' && (
                <div style={{ marginTop: '10px' }}>
                  <label className="question-text">Please provide the name of the person who referred you *</label>
                  <input 
                    type="text" 
                    name="referral_name" 
                    value={formData.referral_name} 
                    onChange={handleChange} 
                    placeholder="Referrer's name"
                    className={formErrors.referral_name ? 'error' : ''}
                  />
                  {formErrors.referral_name && <span className="error-message">{formErrors.referral_name}</span>}
                </div>
              )}

              {formData.how_did_you_hear === 'Other' && (
                <div style={{ marginTop: '10px' }}>
                  <label className="question-text">Please specify how you heard about this role *</label>
                  <input 
                    type="text" 
                    name="other_source" 
                    value={formData.other_source} 
                    onChange={handleChange} 
                    placeholder="How did you hear about this role?"
                    className={formErrors.other_source ? 'error' : ''}
                  />
                  {formErrors.other_source && <span className="error-message">{formErrors.other_source}</span>}
                </div>
              )}
            </div>

            <div className="form-group full-width">
              <label className="question-text">Additional notes (optional)</label>
              <textarea 
                name="additional_notes" 
                value={formData.additional_notes} 
                onChange={handleChange} 
                rows="3"
              />
            </div>
          </div>

          <button 
            type="submit" 
            className="submit-btn"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Application'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default CampusAmbassador2026;

